import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  Image,
  Upload,
  X,
  Megaphone,
} from 'lucide-react';
import api from '../services/api';
import { Button, Input, Checkbox } from '../components/ui';

type CampaignType = 'announcement' | 'advertisement' | 'discount';
type DiscountType = 'percentage' | 'fixed_amount' | 'free_shipping' | 'none';

/** Website shows campaigns only on the homepage carousel below the hero. */
const HOMEPAGE_PLACEMENT = 'homepage_banner' as const;
type LifecycleStatus = 'active' | 'scheduled' | 'expired' | 'disabled';

interface Campaign {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  type: CampaignType;
  placement: string;
  imageUrl?: string;
  linkUrl?: string;
  targetProductId?: string;
  targetCategoryId?: string;
  discountType: DiscountType;
  discountValue: number;
  couponCode?: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  priority: number;
  status: LifecycleStatus;
}

const TYPE_OPTIONS: CampaignType[] = ['announcement', 'advertisement', 'discount'];
const DISCOUNT_OPTIONS: DiscountType[] = ['percentage', 'fixed_amount', 'free_shipping'];

/** Admin picks a destination — no raw URLs required. Empty = app chooses automatically. */
const DESTINATION_OPTIONS: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'destinations.auto' },
  { value: '/shop', labelKey: 'destinations.shop' },
  { value: '/carte', labelKey: 'destinations.cart' },
  { value: '/boutiques', labelKey: 'destinations.boutiques' },
  { value: '/', labelKey: 'destinations.home' },
];

const STATUS_BADGE: Record<LifecycleStatus, string> = {
  active: 'badge-green',
  scheduled: 'badge-yellow',
  expired: 'badge-gray',
  disabled: 'badge-red',
};

const normalizeCampaign = (raw: Record<string, unknown>): Campaign => ({
  id: String(raw.id || raw._id || ''),
  title: String(raw.title || ''),
  subtitle: raw.subtitle as string | undefined,
  description: raw.description as string | undefined,
  type: raw.type as CampaignType,
  placement: String(raw.placement || HOMEPAGE_PLACEMENT),
  imageUrl: raw.imageUrl as string | undefined,
  linkUrl: raw.linkUrl as string | undefined,
  targetProductId: raw.targetProductId ? String(raw.targetProductId) : undefined,
  targetCategoryId: raw.targetCategoryId ? String(raw.targetCategoryId) : undefined,
  discountType: (raw.discountType as DiscountType) || 'none',
  discountValue: Number(raw.discountValue || 0),
  couponCode: raw.couponCode as string | undefined,
  startDate: String(raw.startDate || ''),
  endDate: String(raw.endDate || ''),
  isActive: Boolean(raw.isActive),
  priority: Number(raw.priority ?? 0),
  status: (raw.status as LifecycleStatus) || 'disabled',
});

const emptyForm = {
  title: '',
  subtitle: '',
  description: '',
  type: 'announcement' as CampaignType,
  imageUrl: '',
  linkUrl: '',
  discountType: 'percentage' as DiscountType,
  discountValue: 10,
  couponCode: '',
  startDate: '',
  endDate: '',
  isActive: true,
  priority: 0,
};

const toDatetimeLocal = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Use full local day so "today" is active immediately (avoids start-time-in-future bugs). */
const toCampaignStartISO = (datetimeLocal: string) => {
  const datePart = datetimeLocal.split('T')[0];
  return new Date(`${datePart}T00:00:00`).toISOString();
};

const toCampaignEndISO = (datetimeLocal: string) => {
  const datePart = datetimeLocal.split('T')[0];
  return new Date(`${datePart}T23:59:59.999`).toISOString();
};

const apiErrorMessage = (err: unknown, fallback: string) => {
  const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;
};

const Campaigns: React.FC = () => {
  const { t } = useTranslation(['campaigns', 'common']);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<LifecycleStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<CampaignType | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/admin/marketing-campaigns${qs}`);
      const list = response.data?.data ?? [];
      setCampaigns(Array.isArray(list) ? list.map((c: Record<string, unknown>) => normalizeCampaign(c)) : []);
    } catch (e) {
      console.error(e);
      setError(apiErrorMessage(e, t('messages.loadFailed', { ns: 'campaigns' })));
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        c.title.toLowerCase().includes(q) ||
        c.type.toLowerCase().includes(q) ||
        (c.couponCode || '').toLowerCase().includes(q);
      const matchesType = !typeFilter || c.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [campaigns, searchTerm, typeFilter]);

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (c: Campaign) => {
    setEditing(c);
    setFormData({
      title: c.title,
      subtitle: c.subtitle || '',
      description: c.description || '',
      type: c.type,
      imageUrl: c.imageUrl || '',
      linkUrl: c.linkUrl || '',
      discountType: c.discountType === 'none' ? 'percentage' : c.discountType,
      discountValue: c.discountValue || 0,
      couponCode: c.couponCode || '',
      startDate: toDatetimeLocal(c.startDate),
      endDate: toDatetimeLocal(c.endDate),
      isActive: c.isActive,
      priority: c.priority ?? 0,
    });
    setShowModal(true);
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Max file size is 5MB');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Only JPEG, PNG, GIF, and WebP are allowed');
      return;
    }

    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/upload-single-file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fileUrl = res.data?.data?.fileUrl;
      if (fileUrl) {
        setFormData((p) => ({ ...p, imageUrl: fileUrl }));
      } else {
        throw new Error('Invalid upload response');
      }
    } catch (e) {
      alert(apiErrorMessage(e, t('messages.uploadFailed', { ns: 'campaigns' })));
    } finally {
      setUploadingImage(false);
    }
  };

  const buildPayload = () => ({
    title: formData.title.trim(),
    subtitle: formData.subtitle.trim() || undefined,
    description: formData.description.trim() || undefined,
    type: formData.type,
    placement: HOMEPAGE_PLACEMENT,
    imageUrl: formData.imageUrl.trim() || undefined,
    linkUrl: formData.linkUrl.trim() || undefined,
    targetProductId: null,
    targetCategoryId: null,
    discountType: formData.type === 'discount' ? formData.discountType : 'none',
    discountValue: formData.type === 'discount' ? Number(formData.discountValue) : 0,
    couponCode: formData.couponCode.trim() || undefined,
    startDate: toCampaignStartISO(formData.startDate),
    endDate: toCampaignEndISO(formData.endDate),
    isActive: formData.isActive,
    priority: Number(formData.priority) || 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.startDate || !formData.endDate) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        await api.put(`/admin/marketing-campaigns/${editing.id}`, payload);
      } else {
        await api.post('/admin/marketing-campaigns', payload);
      }
      setShowModal(false);
      setEditing(null);
      await fetchCampaigns();
    } catch (err) {
      alert(apiErrorMessage(err, t('messages.saveFailed', { ns: 'campaigns' })));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (c: Campaign) => {
    if (!window.confirm(t('messages.confirmDelete', { ns: 'campaigns', title: c.title }))) return;
    try {
      await api.delete(`/admin/marketing-campaigns/${c.id}`);
      fetchCampaigns();
    } catch (err) {
      alert(apiErrorMessage(err, t('messages.saveFailed', { ns: 'campaigns' })));
    }
  };

  const toggleActive = async (c: Campaign) => {
    try {
      const path = c.isActive ? 'deactivate' : 'activate';
      await api.patch(`/admin/marketing-campaigns/${c.id}/${path}`);
      fetchCampaigns();
    } catch (err) {
      alert(apiErrorMessage(err, t('messages.saveFailed', { ns: 'campaigns' })));
    }
  };

  const formatPeriod = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime())) return '—';
    return `${s.toLocaleDateString()} – ${e.toLocaleDateString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="text-custom-primary" size={28} />
            {t('title', { ns: 'campaigns' })}
          </h1>
          <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'campaigns' })}</p>
        </div>
        <Button onClick={openCreate} icon={Plus} variant="primary">
          {t('buttons.create', { ns: 'campaigns' })}
        </Button>
      </div>

      {error && (
        <div className="card p-4 bg-red-50 text-red-700 text-sm border border-red-200 rounded-xl">
          {error}
        </div>
      )}

      <div className="card p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <Input
              type="text"
              placeholder={t('search', { ns: 'campaigns' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={Search}
              variant="search"
            />
          </div>
          <select
            className="border-0 bg-gray-100 rounded-2xl px-4 py-3 text-sm text-gray-800"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LifecycleStatus | '')}
            aria-label={t('filterStatus', { ns: 'campaigns' })}
          >
            <option value="">{t('allStatuses', { ns: 'campaigns' })}</option>
            {(['active', 'scheduled', 'expired', 'disabled'] as LifecycleStatus[]).map((s) => (
              <option key={s} value={s}>
                {t(`status.${s}`, { ns: 'campaigns' })}
              </option>
            ))}
          </select>
          <select
            className="border-0 bg-gray-100 rounded-2xl px-4 py-3 text-sm text-gray-800"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as CampaignType | '')}
            aria-label={t('filterType', { ns: 'campaigns' })}
          >
            <option value="">{t('allTypes', { ns: 'campaigns' })}</option>
            {TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {t(`types.${type}`, { ns: 'campaigns' })}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full text-center py-16 text-gray-500 card">
            {searchTerm || statusFilter || typeFilter
              ? t('noSearchResults', { ns: 'campaigns' })
              : t('table.noData', { ns: 'common' })}
            <div className="mt-4">
              <Button onClick={openCreate} icon={Plus} variant="primary">
                {t('buttons.create', { ns: 'campaigns' })}
              </Button>
            </div>
          </div>
        ) : (
          filtered.map((c) => (
            <div key={c.id} className="card p-6 hover:shadow-card-hover transition-shadow">
              <div className="flex gap-4">
                <div className="w-28 h-20 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                  {c.imageUrl ? (
                    <img src={c.imageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image size={24} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg text-gray-900 truncate">{c.title}</h3>
                    <span className={STATUS_BADGE[c.status] || 'badge-gray'}>
                      {t(`status.${c.status}`, { ns: 'campaigns' })}
                    </span>
                  </div>
                  {c.subtitle && <p className="text-sm text-gray-600 line-clamp-1">{c.subtitle}</p>}
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                      {t(`types.${c.type}`, { ns: 'campaigns' })}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                      {t('placements.homepage', { ns: 'campaigns' })}
                    </span>
                    {c.couponCode && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-mono">
                        {t('coupon', { ns: 'campaigns' })}: {c.couponCode}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('period', { ns: 'campaigns' })}: {formatPeriod(c.startDate, c.endDate)}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <Button
                  onClick={() => toggleActive(c)}
                  variant={c.isActive ? 'outline' : 'secondary'}
                  size="sm"
                >
                  {c.isActive
                    ? t('buttons.deactivate', { ns: 'common' })
                    : t('buttons.activate', { ns: 'common' })}
                </Button>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="p-2 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100"
                    title={t('buttons.preview', { ns: 'campaigns' })}
                    onClick={() => {
                      setPreviewCampaign(c);
                      setShowPreview(true);
                    }}
                  >
                    <Eye size={18} />
                  </button>
                  <button
                    type="button"
                    className="p-2 text-primary-600 hover:text-primary-700 rounded-lg hover:bg-gray-100"
                    onClick={() => openEdit(c)}
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:text-red-700 rounded-lg hover:bg-red-50"
                    onClick={() => handleDelete(c)}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-bold text-gray-900">
                {editing
                  ? t('modal.editTitle', { ns: 'campaigns' })
                  : t('modal.createTitle', { ns: 'campaigns' })}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label={t('fields.title', { ns: 'campaigns' })}
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              <Input
                label={t('fields.subtitle', { ns: 'campaigns' })}
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.description', { ns: 'campaigns' })}
                </label>
                <textarea
                  className="w-full bg-gray-100 border-0 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-gray-300"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.type', { ns: 'campaigns' })}
                </label>
                <select
                  className="w-full bg-gray-100 border-0 rounded-2xl px-4 py-3 text-sm"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value as CampaignType })
                  }
                >
                  {TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>
                      {t(`types.${type}`, { ns: 'campaigns' })}
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-sm text-gray-600 bg-gray-50 rounded-2xl px-4 py-3">
                {t('messages.homepageOnly', { ns: 'campaigns' })}
              </p>

              {formData.type === 'discount' && (
                <div className="p-4 bg-amber-50 rounded-2xl space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('fields.discountType', { ns: 'campaigns' })}
                      </label>
                      <select
                        className="w-full bg-white border-0 rounded-2xl px-4 py-3 text-sm"
                        value={formData.discountType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discountType: e.target.value as DiscountType,
                          })
                        }
                      >
                        {DISCOUNT_OPTIONS.map((d) => (
                          <option key={d} value={d}>
                            {t(`discountTypes.${d}`, { ns: 'campaigns' })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Input
                      label={t('fields.discountValue', { ns: 'campaigns' })}
                      type="number"
                      min={0}
                      value={String(formData.discountValue)}
                      onChange={(e) =>
                        setFormData({ ...formData, discountValue: Number(e.target.value) })
                      }
                    />
                  </div>
                  <Input
                    label={t('fields.couponCode', { ns: 'campaigns' })}
                    value={formData.couponCode}
                    onChange={(e) =>
                      setFormData({ ...formData, couponCode: e.target.value.toUpperCase() })
                    }
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.destination', { ns: 'campaigns' })}
                </label>
                <select
                  className="w-full bg-white border-0 rounded-2xl px-4 py-3 text-sm"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                >
                  {DESTINATION_OPTIONS.map((opt) => (
                    <option key={opt.value || 'auto'} value={opt.value}>
                      {t(opt.labelKey, { ns: 'campaigns' })}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {t('fields.destinationHint', { ns: 'campaigns' })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.image', { ns: 'campaigns' })}
                </label>
                <div
                  className={`border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
                    dragActive ? 'border-custom-primary bg-custom-secondary/20' : 'border-gray-300'
                  }`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                >
                  {formData.imageUrl ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.imageUrl}
                        alt=""
                        className="max-h-32 rounded-lg mx-auto"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        onClick={() => setFormData((p) => ({ ...p, imageUrl: '' }))}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                      <p className="text-sm text-gray-600">
                        {t('buttons.uploadImage', { ns: 'campaigns' })}
                      </p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="campaign-image-upload"
                    onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                  />
                  <label
                    htmlFor="campaign-image-upload"
                    className="mt-3 inline-block text-sm text-custom-primary cursor-pointer hover:underline"
                  >
                    {uploadingImage ? '...' : t('buttons.uploadImage', { ns: 'campaigns' })}
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.startDate', { ns: 'campaigns' })}
                  </label>
                  <input
                    type="datetime-local"
                    required
                    className="w-full bg-gray-100 border-0 rounded-2xl px-4 py-3 text-sm"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.endDate', { ns: 'campaigns' })}
                  </label>
                  <input
                    type="datetime-local"
                    required
                    className="w-full bg-gray-100 border-0 rounded-2xl px-4 py-3 text-sm"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </div>
              </div>

              <Input
                label={t('fields.priority', { ns: 'campaigns' })}
                type="number"
                value={String(formData.priority)}
                onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
              />

              <Checkbox
                id="campaign-isActive"
                label={t('fields.isActive', { ns: 'campaigns' })}
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button type="submit" variant="primary" loading={submitting}>
                  {editing ? t('buttons.update', { ns: 'campaigns' }) : t('buttons.create', { ns: 'campaigns' })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && previewCampaign && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <h3 className="font-bold text-lg mb-3">{t('modal.previewTitle', { ns: 'campaigns' })}</h3>
            {previewCampaign.imageUrl && (
              <img
                src={previewCampaign.imageUrl}
                alt=""
                className="w-full rounded-xl mb-3 max-h-44 object-cover"
              />
            )}
            <p className="font-semibold text-gray-900">{previewCampaign.title}</p>
            {previewCampaign.subtitle && (
              <p className="text-gray-600 text-sm mt-1">{previewCampaign.subtitle}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={STATUS_BADGE[previewCampaign.status]}>
                {t(`status.${previewCampaign.status}`, { ns: 'campaigns' })}
              </span>
              <span className="text-xs text-gray-500">
                {t('placements.homepage', { ns: 'campaigns' })}
              </span>
            </div>
            <Button className="mt-6 w-full" variant="primary" onClick={() => setShowPreview(false)}>
              {t('buttons.close', { ns: 'common' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
